import { useState, useRef } from 'react';
import { Plus, Wand2, Image as ImageIcon, X } from 'lucide-react';
import type { CommunicationChannel, Post } from '../../../types';
import { useLanguage } from '../../../contexts/LanguageContext';
import { defaultChannels } from '../../../config/channels';
import { Label } from '../../ui/label';
import { channelIcons, ChannelSvgIcon } from '../../../utils/Constants';
import { Logger } from '../../../utils/logger';

interface MobileCreatePanelProps {
  communicationChannels: CommunicationChannel[];
  onCreate: (post: Post) => void;
  onClose: () => void;
}

export function MobileCreatePanel({ communicationChannels, onCreate, onClose }: MobileCreatePanelProps) {
  const { t } = useLanguage();
  const [selectedChannelId, setSelectedChannelId] = useState(
    communicationChannels.find(c => c.enabled !== false)?.id || ''
  );
  const [content, setContent] = useState('');
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>('');
  const [isImproving, setIsImproving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getChannelIcon = (channel: CommunicationChannel): string => {
    return channelIcons[channel.network] || (channel.network || '?').charAt(0);
  };

  const getNetworkConfig = (channelId: string) => {
    const channel = communicationChannels.find(c => c.id === channelId);
    if (!channel) return null;
    return defaultChannels.find(dc => dc.name === channel.network);
  };

  const handleCreate = () => {
    if (!content.trim() || !selectedChannelId) return;

    const newPost: Post = {
      id: crypto.randomUUID(),
      channelId: selectedChannelId,
      content: content.trim(),
      generatedDate: new Date(),
      imageUrl: uploadedImageUrl || undefined,
      scheduledDate: undefined,
    };

    onCreate(newPost);
    onClose();
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setUploadedImageUrl(imageUrl);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = () => {
    setUploadedImageUrl('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImproveWithAI = async () => {
    if (!content.trim()) return;
    
    setIsImproving(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      const improvedContent = `${content.trim()}\n\n✨ ${t.posts?.improvedByAI || 'Contenu amélioré par l\'IA'}`;
      setContent(improvedContent);
    } catch (error) {
      Logger.error('Error improving content:', error);
    } finally {
      setIsImproving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Channel selection - comme les filtres */}
      <div className="space-y-2">
        <Label className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
          {t.posts?.channel || 'Canal'}
        </Label>
        <div className="flex flex-wrap gap-2">
          {communicationChannels
            .filter(c => c.enabled !== false)
            .map((channel) => {
              const networkConfig = getNetworkConfig(channel.id);
              const isSelected = selectedChannelId === channel.id;
              
              return (
                <button
                  key={channel.id}
                  onClick={() => setSelectedChannelId(channel.id)}
                  className="flex items-center gap-2 transition-opacity hover:opacity-80"
                >
                  <ChannelSvgIcon
                    network={channel.network}
                    size={28}
                    fallbackClassName={isSelected ? `${networkConfig?.color} ${networkConfig?.textColor}` : 'border'}
                    fallbackStyle={!isSelected ? { borderColor: 'var(--color-accent)', color: 'var(--color-accent)', opacity: 0.7 } : undefined}
                  />
                  <span 
                    className="text-sm font-medium truncate"
                    style={{ 
                      color: isSelected ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                      opacity: isSelected ? 1 : 0.7
                    }}
                  >
                    {channel.customName || channel.profileName}
                  </span>
                </button>
              );
            })}
        </div>
      </div>

      {/* Content textarea */}
      <div className="space-y-2">
        <Label htmlFor="content" className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
          {t.posts?.content || 'Contenu'}
        </Label>
        <textarea
          id="content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={t.posts?.contentPlaceholder || 'Tapez votre contenu...'}
          rows={4}
          className="w-full rounded-2xl px-4 py-3 text-sm border resize-none"
          style={{
            borderColor: 'var(--color-border)',
            backgroundColor: 'var(--color-background)',
            color: 'var(--color-text-primary)',
            outline: 'none'
          }}
        />
      </div>

      {/* Action buttons row: Image + Improve */}
      <div className="flex gap-2">
        <button
          onClick={handleImageClick}
          className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-full text-sm transition-all border"
          style={{
            borderColor: 'var(--color-border)',
            color: 'var(--color-text-muted)',
          }}
        >
          <ImageIcon className="w-4 h-4" />
          <span>{uploadedImageUrl ? (t.posts?.replaceImage || 'Remplacer') : (t.posts?.addImage || 'Image')}</span>
        </button>

        <button
          onClick={handleImproveWithAI}
          disabled={!content.trim() || isImproving}
          className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-full text-sm transition-all border"
          style={!content.trim() || isImproving ? {
            borderColor: 'var(--color-border)',
            color: 'var(--color-text-muted)',
            opacity: 0.5,
            cursor: 'not-allowed'
          } : {
            borderColor: 'var(--color-border)',
            color: 'var(--color-text-muted)',
          }}
        >
          <Wand2 className="w-4 h-4" />
          <span>{isImproving ? (t.posts?.improving || 'En cours...') : (t.posts?.improveWithAi || 'Améliorer')}</span>
        </button>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />

      {/* Image preview */}
      {uploadedImageUrl && (
        <div className="relative rounded-2xl overflow-hidden">
          <img 
            src={uploadedImageUrl} 
            alt="Upload preview" 
            className="w-full h-32 object-cover"
          />
          <button
            onClick={handleRemoveImage}
            className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'var(--color-button-text)'
            }}
          >
            <X className="w-4 h-4" style={{ color: 'var(--color-button-text)' }} />
          </button>
        </div>
      )}

      {/* Create button */}
      <button
        onClick={handleCreate}
        disabled={!content.trim() || !selectedChannelId}
        className="w-full py-2 px-3 rounded-full text-sm transition-all flex items-center justify-center gap-2"
        style={content.trim() && selectedChannelId ? {
          backgroundColor: 'var(--color-primary)',
          color: 'var(--color-button-text)',
        } : {
          backgroundColor: 'var(--color-border)',
          color: 'var(--color-text-muted)',
          cursor: 'not-allowed',
          opacity: 0.5
        }}
      >
        <Plus className="w-4 h-4" />
        <span>{t.posts?.createPost || 'Créer le post'}</span>
      </button>
    </div>
  );
}